import {
  Bars3Icon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { NotificationDropdown } from "../ui/NotificationDropdown";

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user } = useAuth();
  const location = useLocation();

  const pageTitle = useMemo(() => {
    const path = location.pathname;
    switch (path) {
      case "/dashboard":
        return "Dashboard";
      case "/feedback":
        return "Feedback Management";
      case "/admin-management":
        return "Admin Management";
      case "/users":
        return "User Management";
      case "/faqs":
        return "FAQ Management";
      case "/topics":
        return "Topics of Interest";
      case "/contact-info":
        return "Contact Information";
      case "/analytics":
        return "Analytics";
      case "/profile":
        return "Profile Management";
      case "/care-centers/register":
        return "Register Care Center";
      default:
        return "Dashboard";
    }
  }, [location.pathname]);

  const breadcrumbs = useMemo(() => {
    const paths = location.pathname.split("/").filter(Boolean);
    const crumbs = [{ name: "Home", path: "/dashboard" }];

    const nameMap: Record<string, string> = {
      "admin-management": "Admin Management",
      users: "User Management",
      faqs: "FAQ Management",
      topics: "Topics of Interest",
      "contact-info": "Contact Information",
      analytics: "Analytics",
      profile: "Profile Management",
      "care-centers": "Care Centers",
      register: "Register",
    };

    paths.forEach((path, index) => {
      const fullPath = "/" + paths.slice(0, index + 1).join("/");
      const name = nameMap[path] || path.charAt(0).toUpperCase() + path.slice(1);
      crumbs.push({ name, path: fullPath });
    });

    return crumbs;
  }, [location.pathname]);

  // Generate avatar URL with fallback
  const getAvatarUrl = () => {
    const username = user?.username || "Admin";
    return (
      user?.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        username
      )}&background=f47842&color=fff&size=128`
    );
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm sticky top-0 z-40 w-full">
      <div className="px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Left side */}
          <div className="flex items-center min-w-0 flex-1">
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 lg:hidden transition-colors duration-200 flex-shrink-0"
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            <div className="ml-2 sm:ml-4 lg:ml-0 min-w-0 flex-1">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white truncate">
                  {pageTitle}
                </h1>
                {/* <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs sm:text-sm font-medium rounded-md border border-yellow-200 dark:border-yellow-800 whitespace-nowrap">
                  Testing Branch
                </span> */}
              </div>

              {/* Breadcrumbs - Hidden on mobile, shown on tablet+ */}
              <nav className="hidden sm:flex mt-1" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  {breadcrumbs.map((crumb, index) => (
                    <li key={crumb.path} className="flex items-center">
                      {index > 0 && (
                        <svg
                          className="flex-shrink-0 h-3 w-3 sm:h-4 sm:w-4 text-gray-300 dark:text-gray-600 mx-1 sm:mx-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      <span
                        className={`transition-colors duration-200 truncate max-w-24 sm:max-w-32 ${
                          index === breadcrumbs.length - 1
                            ? "text-gray-900 dark:text-white font-medium"
                            : "hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
                        }`}
                        title={crumb.name}
                      >
                        {crumb.name}
                      </span>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4 flex-shrink-0">
            {/* Notifications */}
            <NotificationDropdown />

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title={
                isDarkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {isDarkMode ? (
                <SunIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <MoonIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>

            {/* User avatar */}
            <Link
              to="/profile"
              className="flex items-center space-x-2 sm:space-x-3 pl-2 sm:pl-3 border-l border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg px-2 py-1 transition-colors duration-200"
            >
              <img
                className="h-7 w-7 sm:h-8 sm:w-8 rounded-full ring-2 ring-primary-500"
                src={getAvatarUrl()}
                alt={user?.username || "Admin"}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-24 lg:max-w-none">
                  {user?.username || "Admin"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user?.role || "admin"}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
